/**
 * 🔒 Armadillo Adamantium Next - Server-Side Enforced Security Mesh
 * 14-Layer protection with server-side enforcement.
 *
 * Phoenix Architecture: Bridge → Edge Middleware → Edge Function → Database
 *
 * New in Next:
 * - Server-side security pipeline (enforced at edge, not bypassable)
 * - Behavioral biometrics (keystroke, mouse, scroll patterns)
 * - DNA-level device fingerprinting
 * - Supply chain integrity monitoring
 * - Automated incident response
 * - Predictive attack forecasting
 * - Real X25519 ECDH key exchange (replaces fake ML-KEM)
 * - Server-side rate limiting (replaces localStorage)
 * - DDoS Fortress: Shannon entropy + Count-Min Sketch + adaptive PoW
 * - Advanced WAF: multi-pass normalization, payload padding, smuggling detection
 */

import { sha256 } from 'js-sha256';
import type { 
  SecurityContext, TrustTierName, CapabilityToken, ThreatCapsule, 
  ZKEvent, GodelChallenge, GodelResponse, MLKEMKeyPair
} from './armadillo/types';

// ============================================
// LAYER 1: Gateway Edge Engine (GEE)
// ============================================
import {
  extractClientSignals, isBot, calculateEntropy
} from './armadillo/gee';

// ============================================
// LAYER 2: Capability Trust Engine (CTE)
// ============================================
import {
  issueCapability, verifyToken, hasScope,
  getTrustTierFromToken, refreshToken, revokeToken, isTokenRevoked
} from './armadillo/cte';

// ============================================
// LAYER 3: Adaptive Risk Engine (ARE)
// ============================================
import {
  computeRisk, recordBlock, recordRequest
} from './armadillo/are';

// ============================================
// LAYER 4: Threat Capsule Network (TCN)
// ============================================
import {
  createCapsule, queryThreatCapsules, gossipToPeers, cleanupExpiredCapsules
} from './armadillo/tcn';

// ============================================
// LAYER 5: VRF Reflection Shield (VRS) — REWRITTEN
// ============================================
import {
  generateVRFProof, verifyVRFProof, shouldReflect,
  generateReflectionChallenge, solveReflectionChallenge, verifyReflectionChallenge,
  calculateClientPressure, buildReflectionResponse
} from './armadillo/vrs';

// ============================================
// LAYER 6: Homomorphic Rate Limiter (HRL)
// ============================================
import {
  checkRateLimit, resetRateLimit, getRateLimitStatus
} from './armadillo/hrl';

// ============================================
// LAYER 7: Gödel Challenge Module
// ============================================
import {
  generateGodelChallenge, verifyGodelResponse,
  hasBeenSolvedRecently, markChallengeSolved
} from './armadillo/godel';

// ============================================
// LAYER 8: Zero-Knowledge Compliance (ZKCL)
// ============================================
import {
  logSecurityEvent as zkcl_logSecurityEvent,
  getRecentEvents as zkcl_getRecentEvents,
  exportComplianceData as zkcl_exportData
} from './armadillo/zkcl';

// ============================================
// LAYER 9: Post-Quantum Handshake (QRH)
// ============================================
import {
  generateMLKEMKeyPair, performHandshake, deriveSessionKey,
  encryptMessage, decryptMessage
} from './armadillo/qrh';

// ============================================
// LAYER 10: Security Policy Engine (SPE)
// ============================================
import {
  loadCachedPolicy, evaluatePolicy as spe_evaluatePolicy,
  trustTierFromScore
} from './armadillo/spe';

// ============================================
// CLIENT SDK
// ============================================
import {
  generateFingerprint, generateEntropyNonce, humanDelay,
  armadilloFetch, initializeSession, clearSession, healthCheck
} from './armadillo/client-sdk';

// ============================================
// NEW LAYERS: Armadillo Next
// ============================================
import { behavioralBiometrics } from './armadillo/behavioral-biometrics';
import { deviceFingerprint } from './armadillo/device-fingerprint';
import { supplyChainMonitor } from './armadillo/supply-chain-monitor';
import { incidentResponse, attackForecaster } from './armadillo/incident-response';

// ============================================
// LAYER 13: DDoS Fortress (Mathematical DDoS Defense)
// ============================================
import {
  recordRequestEntropy, computeTrafficEntropy, computeDDoSScore,
  cmsUpdate, cmsQuery, cusumDetect, solveAdaptivePoW, analyzeTimingVariance
} from './armadillo/ddos-fortress';

// ============================================
// LAYER 14: Advanced WAF (Mathematical Firewall)
// ============================================
import {
  analyzePayload, deepNormalize, ngramAnalysis,
  detectPayloadPadding, detectRequestSmuggling, detectCRLFInjection,
  detectPolyglot, semanticAnalysis
} from './armadillo/advanced-waf';

// ============================================
// SECURITY MONITOR
// ============================================
import { securityMonitor } from '@/services/securityMonitor';

// ============================================
// TOKEN STORAGE
// ============================================

function getTokenFromStorage(): CapabilityToken | null {
  try {
    const raw = localStorage.getItem('armadillo_capability_token');
    if (!raw) return null;
    const parts = raw.split('.');
    return { payload: parts[0], signature: parts[1] || '', raw };
  } catch { return null; }
}

function saveTokenToStorage(token: CapabilityToken): void {
  localStorage.setItem('armadillo_capability_token', token.raw);
}

// ============================================
// CLIENT SECURITY CONTEXT
// ============================================

// Native WebCrypto SHA-256 (hex) — hardware-accelerated, far cheaper than the
// pure-JS fallback on low-end devices. js-sha256 stays for sync call sites.
async function fastSha256(input: string): Promise<string> {
  try {
    if (crypto?.subtle) {
      const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(input));
      return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('');
    }
  } catch { /* fall through to pure-JS */ }
  return sha256(input);
}

// Fingerprint inputs only change when UA/language/hardware/screen change, so
// cache the derived hashes for the session instead of re-hashing on every
// pipeline run (forms call this on each action).
let _fpCacheKey = '';
let _fpCache: { fingerprintHash: string; ipHash: string; sessionId: string } | null = null;

export async function buildClientSecurityContext(
  userId: string | null,
  action: string
): Promise<SecurityContext> {
  const now = Date.now();
  const ua = navigator.userAgent;

  const fpRaw = `${ua}|${navigator.language}|${navigator.hardwareConcurrency}|${screen.width}x${screen.height}`;
  const cacheKey = fpRaw + '|' + (userId || 'anon');
  if (_fpCache && _fpCacheKey === cacheKey) {
    return buildContextFromCache(_fpCache, userId, action, now, ua);
  }

  const fpHash = await fastSha256(fpRaw + 'armadillo-fp-salt');
  const ipHash = await fastSha256(fpHash);
  const sessionId = await fastSha256(fpHash + (userId || 'anon'));

  _fpCacheKey = cacheKey;
  _fpCache = { fingerprintHash: fpHash, ipHash, sessionId };

  return buildContextFromCache(_fpCache, userId, action, now, ua);
}

async function buildContextFromCache(
  hashes: { fingerprintHash: string; ipHash: string; sessionId: string },
  userId: string | null,
  action: string,
  now: number,
  ua: string
): Promise<SecurityContext> {
  let entropyNonce = localStorage.getItem('armadillo_entropy_nonce');
  if (!entropyNonce) {
    entropyNonce = crypto.randomUUID();
    localStorage.setItem('armadillo_entropy_nonce', entropyNonce);
  }

  const token = getTokenFromStorage();

  return {
    requestId: crypto.randomUUID(),
    timestamp: now,
    ip: 'client',
    ipHash: hashes.ipHash,
    fingerprintHash: hashes.fingerprintHash,
    userAgent: ua,
    path: action,
    method: 'CLIENT',
    riskScore: 0,
    trustTier: 'untrusted' as TrustTierName,
    entropyNonce,
    sessionId: hashes.sessionId,
    capabilityToken: token?.raw ?? null,
    headers: { 'user-agent': ua, 'accept-language': navigator.language }
  };
}

// ============================================
// THREAT DETECTION
// ============================================

function detectThreat(input: string): string | null {
  if (/(\bSELECT\b|\bINSERT\b|\bUPDATE\b|\bDELETE\b|\bDROP\b|\bUNION\b|\bALTER\b)/i.test(input) &&
      /(--|\/\*|\*\/|;)/.test(input)) return 'sql_injection';
  if (/<script[^>]*>/i.test(input) || /javascript:/i.test(input) || /on\w+\s*=/i.test(input)) return 'xss';
  if (/\.\.\//.test(input) || /\.\.\\/.test(input)) return 'path_traversal';
  if (/[;&|`$]/.test(input) && (/\$\(/.test(input) || /`[^`]*`/.test(input))) return 'command_injection';
  return null;
}

// ============================================
// INITIALIZATION
// ============================================

let _initialized = false;

export async function initializeArmadillo(userId?: string): Promise<void> {
  if (_initialized) return;



  // L2: Issue capability token
  const existingToken = getTokenFromStorage();
  if (!existingToken) {
    const ctx = await buildClientSecurityContext(userId || 'anon', 'init');
    const newToken = await issueCapability(ctx, ['bounty:*', 'profile:*', 'message:*']);
    saveTokenToStorage(newToken);
  }

  // L9: Post-quantum handshake (Real X25519 ECDH)
  try {
    const keys = await generateMLKEMKeyPair();
    const handshake = await performHandshake(keys.publicKey);
    const sessionKey = await deriveSessionKey(handshake.sharedSecret, userId || 'anon', 'encrypt');
    localStorage.setItem('armadillo_pq_key', JSON.stringify(Array.from(sessionKey)));

  } catch (e) {

  }

  // L10: Load security policy
  loadCachedPolicy();

  // Client SDK: session setup
  await initializeSession();

  // Start client-side security monitoring
  try {
    // Check environment threats on init
    const envThreats = securityMonitor.checkEnvironmentThreats();
    for (const threat of envThreats) {
      if (threat.scan_detected) {

        await zkcl_logSecurityEvent({
          timestamp: Date.now(),
          riskScore: threat.risk_level === 'high' ? 0.8 : 0.4,
          decision: 'block',
          policyHash: 'armadillo-v1'
        });
      }
    }

    // Check for framing attacks
    if (securityMonitor.detectFramingAttack()) {

    }


  } catch (e) {

  }

  // ============================================
  // NEW LAYERS: Start next-gen security modules
  // ============================================

  // L11: Behavioral Biometrics — start collecting user behavior
  try {
    behavioralBiometrics.start();

  } catch (e) {

  }

  // L12: Device Fingerprinting — collect device signals
  try {
    await deviceFingerprint.collectFingerprint();

  } catch (e) {

  }

  // Supply Chain Monitor — watch for DOM tampering
  try {
    supplyChainMonitor.start();

  } catch (e) {

  }

  // L13: DDoS Fortress — start client-side entropy tracking
  try {
    // Record initial request for entropy baseline
    recordRequestEntropy('init', '/init', navigator.userAgent);

  } catch (e) {

  }

  // L14: Advanced WAF — pre-warm normalization engine
  try {
    // Test deep normalization is available
    deepNormalize('test%20input');

  } catch (e) {

  }

  _initialized = true;

}

// ============================================
// GÖDEL CHALLENGE VERIFICATION
// ============================================

// ============================================
// FULL SECURITY PIPELINE
// ============================================

export interface ArmadilloResult {
  allowed: boolean;
  reason?: string;
  riskScore?: number;
  trustTier?: TrustTierName;
  challenge?: any;
  capsuleId?: string;
  layerBlocked?: string;
}

export async function runSecurityPipeline(
  userId: string | null,
  action: string,
  input?: string
): Promise<ArmadilloResult> {
  if (!_initialized) await initializeArmadillo(userId || undefined);

  const ctx = await buildClientSecurityContext(userId, action);

  // Track request in security monitor
  try {
    const scanResult = securityMonitor.trackRequest(action);
    if (scanResult.scan_detected) {

      
      if (scanResult.risk_level === 'high') {
        await securityMonitor.logAlert(
          scanResult.scan_type!,
          'high',
          `High-frequency scanning detected: ${scanResult.details}`
        );
      }
    }
  } catch { /* Monitor non-critical */ }

  // L3: Adaptive Risk Engine
  const riskScore = await computeRisk(ctx);
  ctx.riskScore = riskScore;

  // L1: Extract signals & bot check
  try {
    const signals = extractClientSignals(ctx.userAgent as any);
    const bot = isBot(ctx.userAgent);
    if (bot && riskScore > 0.3) {
      return { allowed: false, riskScore, trustTier: 'untrusted', reason: 'Bot detected', layerBlocked: 'L1-GEE' };
    }
  } catch { /* L1 not critical */ }

  // L2: Verify capability token
  const token = getTokenFromStorage();
  if (token) {
    try {
      const tokenValid = await verifyToken(token.raw, ctx);
      if (!tokenValid) {
        const revoked = await isTokenRevoked(token.raw);
        if (revoked) return { allowed: false, riskScore, trustTier: 'untrusted', reason: 'Token revoked', layerBlocked: 'L2-CTE' };
      }
    } catch { /* Continue */ }
  }

  // L4: Threat detection + capsules
  if (input) {
    const threatType = detectThreat(input);
    if (threatType) {

      try {
        const capsule = await createCapsule({
          type: threatType as any,
          ipHash: ctx.ipHash,
          signals: { input: input.substring(0, 200) },
          confidence: 0.9
        });
        await gossipToPeers(capsule);
      } catch { /* Best effort */ }

      try {
        await zkcl_logSecurityEvent({
          timestamp: Date.now(),
          riskScore: 1.0,
          decision: 'block',
          policyHash: 'armadillo-v1'
        });
      } catch { /* Best effort */ }

      return { allowed: false, riskScore: 1.0, trustTier: 'untrusted', reason: `${threatType} blocked`, layerBlocked: 'L4-TCN' };
    }
  }

  // L5: VRF Reflection Shield
  if (riskScore > 0.7) {
    const reflection = await shouldReflect(ctx, riskScore);
    if (reflection.reflect) {

      const challenge = generateReflectionChallenge(ctx, riskScore);
      return { allowed: false, riskScore, trustTier: 'untrusted', reason: reflection.reason, challenge, layerBlocked: 'L5-VRS' };
    }
  }

  // L6: Homomorphic Rate Limiter - Differentiate by action type
  try {
    // ARMADILLO: Action-specific rate limits
    const rateLimits: Record<string, { max: number; window: number }> = {
      'bounty_creation': { max: 10, window: 3600 },      // 10 bounties/hour
      'claim_request': { max: 20, window: 3600 },         // 20 claims/hour
      'proof_submission': { max: 15, window: 3600 },      // 15 proofs/hour
      'message_send': { max: 30, window: 3600 },          // 30 messages/hour
      'default': { max: 100, window: 3600 }               // 100 generic actions/hour
    };
    
    const limit = rateLimits[action] || rateLimits['default'];
    const rateResult = await checkRateLimit(ctx.sessionId, limit.max, limit.window);
    
    if (!rateResult.allowed) {

      return { allowed: false, riskScore, trustTier: ctx.trustTier as TrustTierName, reason: `Rate limit exceeded for ${action.replace('_', ' ')}`, layerBlocked: 'L6-HRL' };
    }
  } catch { /* Continue */ }

  // L7: Gödel Challenge (for medium-risk) - Generate and log for verification
  if (riskScore > 0.5) {
    try {
      const challenge = generateGodelChallenge(Math.floor(Math.random() * 1000000), 'easy');
      
      // ARMADILLO: Log challenge for client-side verification
      // Note: Full verification requires client to solve and return response
      // For now, we generate and log the challenge, but allow the action
      // TODO: Implement challenge-response roundtrip for strict verification

      
      // Store challenge in session for potential verification
      sessionStorage.setItem('armadillo_godel_challenge', JSON.stringify(challenge));
    } catch { /* Not critical */ }
  }

  // L8: Log event
  try {
    await zkcl_logSecurityEvent({
      timestamp: Date.now(),
      riskScore,
      decision: 'allow',
      policyHash: 'armadillo-v1'
    });
  } catch { /* Best effort */ }

  // L10: Policy evaluation
  try {
    const policyResult = await spe_evaluatePolicy(ctx);
    if (!policyResult.allowed) {
      return { allowed: false, riskScore, trustTier: ctx.trustTier as TrustTierName, reason: policyResult.reason, layerBlocked: 'L10-SPE' };
    }
  } catch { /* Continue */ }

  // Assign trust tier
  const trustTier = trustTierFromScore(riskScore);
  ctx.trustTier = trustTier;

  // Record for analytics
  await recordRequest(ctx.ipHash);


  return { allowed: true, riskScore, trustTier };
}

// ============================================
// QUICK VALIDATION (instant, no async)
// ============================================

export function quickValidate(input: string): { valid: boolean; reason?: string } {
  if (!input) return { valid: true };
  if (input.includes('\0')) return { valid: false, reason: 'Null byte' };
  if (input.length > 50000) return { valid: false, reason: 'Too long' };
  return { valid: true };
}

// ============================================
// ARMADILLO CLIENT OBJECT
// ============================================

export const armadilloClient = {
  initialize: initializeArmadillo,
  runPipeline: runSecurityPipeline,
  buildContext: buildClientSecurityContext,
  quickValidate,

  // Client SDK
  generateFingerprint, generateEntropyNonce, humanDelay, armadilloFetch,
  initializeSession, clearSession, healthCheck,

  // Layer utilities
  verifyToken: (token: string) => {
    // Client-side: build minimal context for verification
    const miniCtx: SecurityContext = {
      requestId: '', timestamp: 0, ip: '', ipHash: '', fingerprintHash: '',
      userAgent: '', path: '', method: '', riskScore: 0, trustTier: 'untrusted',
      entropyNonce: '', sessionId: '', capabilityToken: token
    };
    return verifyToken(token, miniCtx);
  },
  issueCapability,
  computeRisk,
  createCapsule,
  gossipToPeers,
  checkRateLimit,
  resetRateLimit,
  generateGodelChallenge,
  verifyGodelResponse,
  trustTierFromScore,
  loadCachedPolicy,
  generateVRFProof,
  verifyVRFProof,
  generateMLKEMKeyPair,
  performHandshake,
  encryptMessage,
  decryptMessage,
  deriveSessionKey,
  cleanupExpiredCapsules,
};
